import { useEffect, useRef } from 'react'
import { useAppSelector } from '@store/hooks'
import {
  clearAcquisitionContextApi,
  upsertAcquisitionContextApi,
} from '@services/api/acquisition-context.api'

/**
 * Publish the patient a clinician currently has open to the active-patient
 * acquisition context for their selected operatory/chair, so incoming DICOM
 * images route to that patient (operatory-keyed routing).
 *
 * - Publishes (PUT) whenever both a chair and a patient are set.
 * - Periodically re-publishes to slide the server-side TTL while the chart
 *   stays open.
 * - Clears (DELETE) when the patient closes, the chair is unset/changed, or the
 *   component unmounts.
 *
 * No-ops gracefully when no chair is selected. All failures are swallowed
 * (best-effort) so routing never blocks the clinical UI.
 */

// Re-publish every 5 minutes to keep the server-side sliding TTL fresh while a
// chart stays open longer than the default acquisition-context TTL (~20 min).
const REFRESH_INTERVAL_MS = 5 * 60 * 1000

const getCurrentUserId = (): string | null => {
  try {
    const raw = localStorage.getItem('user')
    if (!raw) return null
    const parsed = JSON.parse(raw) as { user_id?: string }
    return parsed?.user_id || null
  } catch {
    return null
  }
}

const clearChair = (operatoryId: string): void => {
  void clearAcquisitionContextApi(operatoryId).catch((error) => {
    console.warn('[active-patient] failed to clear acquisition context', error)
  })
}

export const usePublishActivePatient = (
  patientId: string | null | undefined,
): void => {
  const operatoryId = useAppSelector(
    (s) => s.operatorySession.selectedOperatoryId,
  )

  // The operatory we last published to, so we can clear the correct chair when
  // the selection changes, the chart closes, or the component unmounts.
  const publishedOperatoryRef = useRef<string | null>(null)

  useEffect(() => {
    // Nothing to publish: clear any chair we previously claimed.
    if (!operatoryId || !patientId) {
      const prev = publishedOperatoryRef.current
      if (prev) {
        publishedOperatoryRef.current = null
        clearChair(prev)
      }
      return
    }

    // Chair changed while a chart stayed open — release the old chair. (Same
    // chair with a new patient is handled by the upsert below, which overwrites
    // the row in place, avoiding a delete/publish race.)
    const prev = publishedOperatoryRef.current
    if (prev && prev !== operatoryId) clearChair(prev)

    let cancelled = false
    const publish = async (): Promise<void> => {
      try {
        await upsertAcquisitionContextApi({
          operatory_id: operatoryId,
          patient_id: patientId,
          set_by_user_id: getCurrentUserId(),
        })
        if (!cancelled) publishedOperatoryRef.current = operatoryId
      } catch (error) {
        console.warn(
          '[active-patient] failed to publish acquisition context',
          error,
        )
      }
    }

    void publish()
    const interval = window.setInterval(
      () => void publish(),
      REFRESH_INTERVAL_MS,
    )

    return () => {
      cancelled = true
      window.clearInterval(interval)
    }
  }, [operatoryId, patientId])

  // Clear on unmount (e.g. navigating away from the viewer = patient closed).
  useEffect(() => {
    return () => {
      const prev = publishedOperatoryRef.current
      if (prev) {
        publishedOperatoryRef.current = null
        clearChair(prev)
      }
    }
  }, [])
}
